import dayjs from "dayjs";
import i18next from "i18next";

const getDateTimeFromMonth = (monthName) => {
  const currentYear = new Date().getFullYear();
  const monthMap = {
    January: "01",
    February: "02",
    March: "03",
    April: "04",
    May: "05",
    June: "06",
    July: "07",
    August: "08",
    September: "09",
    October: "10",
    November: "11",
    December: "12",
  };

  const month = monthMap[monthName] || "01";
  return `${currentYear}-${month}-02T08:02:26.380Z`;
};

const getBorderColor = (maxMinMean, value_type) => {
  if (value_type === "forecast") {
    return "rgb(10, 9, 235)";
  }
  if (maxMinMean === "max") {
    return "rgb(238, 84, 30)";
  }
  if (maxMinMean === "min") {
    return "rgb(238, 84, 30)";
  }
  return "rgb(20, 230, 121)";
};

const getBackgroundColor = (maxMinMean, value_type) => {
  if (value_type === "forecast") {
    return "rgba(171,215,230,0.6)";
  }
  if (maxMinMean === "max") {
    return "rgba(128,128,128,0.3)"; // Grigio semi-trasparente per l'area tra min e max
  }
  return "rgba(228,229,231,0.6)";
};

const getBorderDash = (value_type) => {
  if (value_type === "forecast") {
    return [5, 5];
  }
  return [];
};

export const convertDataForChart = (responseData) => {
  if (
    !responseData ||
    !responseData.modelOutputs ||
    !responseData.historicalData
  ) {
    return { data: [] };
  }

  const convertedData = [];

  responseData.historicalData.data.forEach((item) => {
    if (item.measure === "volume") {
      convertedData.push({
        datetime: getDateTimeFromMonth(item.datetime),
        measure: "storage",
        unit: "1000 M3",
        value_type: "real",
        min: item.min,
        max: item.max,
        mean: item.mean.toString(),
      });
    }

    if (item.measure === "inflow") {
      convertedData.push({
        datetime: getDateTimeFromMonth(item.datetime),
        measure: "inflow",
        unit: "M3/S",
        value_type: "real",
        min: item.min,
        max: item.max,
        mean: item.mean.toString(),
      });
    }

    if (item.measure === "outflow") {
      convertedData.push({
        datetime: getDateTimeFromMonth(item.datetime),
        measure: "combined outflow (Irrigation Demand + Urban Demand)",
        unit: "M3/S",
        value_type: "real",
        min: item.min,
        max: item.max,
        mean: item.mean.toString(),
      });
    }
  });

  responseData.modelOutputs.data.forEach((item) => {
    let measure = item.measure;
    let unit = item.unit;

    if (measure === "storage") {
      unit = "1000 M3";
    }

    convertedData.push({
      datetime: item.datetime,
      measure: measure,
      unit: unit,
      value_type: "forecast",
      min: item.min,
      max: item.max,
      mean: item.mean.toString(),
    });
  });

  return { data: convertedData };
};

export const generateDataSetFromDataModel = (dataFromModel) => {
  const { t } = i18next;
  if (!dataFromModel?.data) {
    return { labels: [], datasets: [] };
  }

  // Trova la prima data di forecast per determinare il punto di centratura
  const firstForecastDate = dataFromModel.data
    .filter((item) => item.value_type === "forecast")
    .sort((a, b) =>
      dayjs(a.datetime).isBefore(dayjs(b.datetime)) ? -1 : 1,
    )[0]?.datetime;

  const centerDate = firstForecastDate ? dayjs(firstForecastDate) : dayjs();

  // Generiamo una finestra temporale di 12 mesi centrata sulla prima data forecast
  const startDate = centerDate.subtract(6, "months");
  const endDate = centerDate.add(6, "months");

  const monthsInRange = [];
  let currentDate = startDate.startOf("month");

  while (
    currentDate.isBefore(endDate) ||
    currentDate.isSame(endDate, "month")
  ) {
    const monthName = currentDate.format("MMMM");
    monthsInRange.push({
      monthName: monthName,
      year: currentDate.format("YYYY"),
      labelKey: `${t(`months.${monthName}`)} ${currentDate.format("YYYY")}`,
      date: currentDate.clone(),
    });
    currentDate = currentDate.add(1, "month");
  }

  const sortedLabels = monthsInRange.map((m) => m.labelKey);

  // Funzione per ottenere dati storici per un mese specifico
  const getHistoricalDataForMonth = (monthInfo, measure, mmm) => {
    const targetMonth = monthInfo.date.format("MMMM");

    const historicalItem = dataFromModel.data.find((item) => {
      if (item.value_type !== "real" || item.measure !== measure) {
        return false;
      }

      const itemMonth = dayjs(item.datetime).format("MMMM");
      return itemMonth === targetMonth;
    });

    return historicalItem ? Number(historicalItem[mmm]) : null;
  };

  // Raggruppiamo i dati
  const groupedData = new Map();

  // Prima aggiungiamo tutti i dati reali e forecast esistenti
  dataFromModel.data.forEach((element) => {
    const elementDate = dayjs(element.datetime);
    const labelKey = `${t(
      `months.${elementDate.format("MMMM")}`,
    )} ${elementDate.format("YYYY")}`;

    // Controlla se questa data è nella nostra finestra temporale
    const labelIndex = sortedLabels.indexOf(labelKey);
    if (labelIndex === -1) return; // Skip se non è nella finestra

    const groupKey = `${element.measure}_${element.value_type}`;

    if (!groupedData.has(groupKey)) {
      groupedData.set(groupKey, {
        measure: element.measure,
        value_type: element.value_type,
        max: new Array(sortedLabels.length).fill(null),
        min: new Array(sortedLabels.length).fill(null),
        mean: new Array(sortedLabels.length).fill(null),
      });
    }

    const group = groupedData.get(groupKey);
    group.max[labelIndex] = Number(element.max);
    group.min[labelIndex] = Number(element.min);
    group.mean[labelIndex] = Number(element.mean);
  });

  // Ora riempiamo i dati storici mancanti ripetendo i pattern stagionali
  const measures = [...new Set(dataFromModel.data.map((item) => item.measure))];

  measures.forEach((measure) => {
    const realGroupKey = `${measure}_real`;

    if (!groupedData.has(realGroupKey)) {
      groupedData.set(realGroupKey, {
        measure: measure,
        value_type: "real",
        max: new Array(sortedLabels.length).fill(null),
        min: new Array(sortedLabels.length).fill(null),
        mean: new Array(sortedLabels.length).fill(null),
      });
    }

    const group = groupedData.get(realGroupKey);

    // Riempi i valori mancanti con dati storici corrispondenti
    monthsInRange.forEach((monthInfo, index) => {
      if (group.max[index] === null) {
        // Se non abbiamo dati per questo mese/anno, usa i dati storici del mese
        group.max[index] = getHistoricalDataForMonth(monthInfo, measure, "max");
        group.min[index] = getHistoricalDataForMonth(monthInfo, measure, "min");
        group.mean[index] = getHistoricalDataForMonth(
          monthInfo,
          measure,
          "mean",
        );
      }
    });
  });

  const datasets = [];

  groupedData.forEach((group, groupKey) => {
    const { measure, value_type } = group;

    if (value_type === "real") {
      // Dataset per MIN
      datasets.push({
        label: `${t(`chart.legend.${measure}`)} ${t("chart.legend.min")}`,
        data: group.min,
        borderColor: getBorderColor("min", value_type),
        backgroundColor: "transparent",
        fill: false,
        pointRadius: 2,
        pointHoverRadius: 4,
        borderWidth: 2,
      });

      // Dataset per MAX con fill
      datasets.push({
        label: `${t(`chart.legend.${measure}`)} ${t("chart.legend.max")}`,
        data: group.max,
        borderColor: getBorderColor("max", value_type),
        backgroundColor: getBackgroundColor("max", value_type),
        fill: "-1",
        pointRadius: 2,
        pointHoverRadius: 4,
        borderWidth: 2,
      });

      // Dataset per MEAN
      datasets.push({
        label: `${t(`chart.legend.${measure}`)} ${t("chart.legend.mean")}`,
        data: group.mean,
        borderColor: getBorderColor("mean", value_type),
        backgroundColor: "transparent",
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      });
    } else {
      // Per i forecast
      ["min", "max", "mean"].forEach((mmm) => {
        const label = `${t(`chart.legend.${measure}`)} ${t(
          `chart.legend.${mmm}`,
        )} (${t("chart.legend.forecast")})`;

        datasets.push({
          label,
          data: group[mmm],
          borderColor: getBorderColor(mmm, value_type),
          backgroundColor: getBackgroundColor(mmm, value_type),
          borderDash: getBorderDash(value_type),
          fill: false,
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
        });
      });
    }
  });

  return { labels: sortedLabels, datasets };
};
